import { Navigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { useEffect, useState } from 'react'
import { authApi, mergeSubscriptionParameters } from '@/api/auth'
import { Box, Card, CardContent, Skeleton } from '@mui/material'
import { DashboardLayout } from '@/components/layout/DashboardLayout'

interface TeacherRouteProps {
  children: React.ReactNode
}

export function TeacherRoute({ children }: TeacherRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const user = useAuthStore((state) => state.user)
  const setAuth = useAuthStore((state) => state.setAuth)
  const [isLoading, setIsLoading] = useState(true)
  const [hasTeacherRole, setHasTeacherRole] = useState(false)

  useEffect(() => {
    const checkRole = async () => {
      if (!isAuthenticated) {
        setIsLoading(false)
        return
      }

      try {
        // Если у пользователя нет ролей в store, загружаем профиль
        if (!user?.roles || user.roles.length === 0) {
          const profile = await authApi.getProfile()
          const token = useAuthStore.getState().token
          // Обновляем пользователя в store
          if (token) {
            const merged = await mergeSubscriptionParameters(profile)
            setAuth(merged, token)
          }
          
          // Проверяем роль teacher
          const teacherRole = profile.roles?.find(
            (role) => role.slug === 'teacher'
          )
          setHasTeacherRole(!!teacherRole)
        } else {
          // Проверяем роль из store
          const teacherRole = user.roles.find(
            (role) => role.slug === 'teacher'
          )
          setHasTeacherRole(!!teacherRole)
        }
      } catch (error) {
        console.error('Error checking role:', error)
        setHasTeacherRole(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkRole()
  }, [isAuthenticated, user, setAuth])

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />
  }

  if (isLoading) {
    return (
      <DashboardLayout>
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Skeleton variant="text" width="40%" height={28} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="80%" height={24} />
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Skeleton variant="text" width="30%" height={28} sx={{ mb: 2 }} />
              <Skeleton variant="text" width="100%" height={24} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="60%" height={24} />
            </CardContent>
          </Card>
        </Box>
      </DashboardLayout>
    )
  }

  if (!hasTeacherRole) {
    return <Navigate to={ROUTES.DASHBOARD} replace />
  }

  return <>{children}</>
}
